import React, { useMemo, useState, useEffect } from 'react';
import CoachesHeader from './CoachesHeader';
import CoachesSidebar from './CoachesSidebar';
import CoachesCRM from '../../../components/crm/CoachesCRM';
import IntroToCostingAndManAcc from '../../../components/training/IntroToCostingAndManAcc';
import DesignABusiness from '../../../components/training/DesignABusiness';
import CapacityBuildingProgram from '../../../components/training/CapacityBuildingProgram';
import LogoutConfirmation from '../../../components/ui/LogoutConfirmation';
import { useAuth } from '../../../context/AuthContext';
import ProgramHealthCheckDashboard from '../../../components/Programs/ProgramHealthCheckDashboard';
import ScalingModules from '../../../components/Modules/ScalingModules';
import GrantBasics from '../../../components/Funding/GrantBasics';
import ApplicationTutorials from '../../../components/Funding/ApplicationTutorials';
import PublicChat from '../../../components/PublicChat/PublicChat';
import Mailbox from '../../../components/Mailbox/Mailbox';
import ProfilingAnalyticsDashboard from '../../../components/ProfilingForms/ProfilingAnalyticsDashboard';
import {
  SessionCalendar,
  SessionHistory,
  SessionNotes,
  FollowUpTasks,
  CoachingTools,
  BestPractices,
  KnowledgeBase,
  Templates,
} from '../../../components/coaches';
import ClientDocumentBrowser from '../../../components/DocumentBank/ClientDocumentBrowser';
import EnhancedTeamManager from '../../../components/team/EnhancedTeamManager';
import SettingsPageComponent from '../../main/SettingsPage';
import Commercialisation from '../../../components/Commercialisation/Commercialisation';
import CommercialisationReadinessChecklist from '../../../components/Commercialisation/CommercialisationReadinessChecklist';
import GTMStrategyPlan from '../../../components/Commercialisation/GTMStrategyPlan';
import StartupLaunchChecklist from '../../../components/Commercialisation/StartupLaunchChecklist';
import AdminAnalyticsDashboard from '../../../components/Commercialisation/AdminAnalyticsDashboard';
import AdminPanel from '../../../components/Commercialisation/AdminPanel';
import ProductDevelopment from '../../../components/ProductDevelopment/ProductDevelopment';
import HealthDashboard from '../../../components/HealthCheck/HealthDashboard';
import MonthlyCoachingReport from '../../../components/Reports/MonthlyCoachingReport';
import JunkDashboard from '../../../components/SharedFeature/JunkDashboard';
import ClientsTable from '../../features/ClientsTable';
import AlumniTable from '../../features/AlumniTable';
import ModeratorsTable from '../../features/ModeratorsTable';
import {
  HiUsers,
  HiCalendarDays,
  HiChartBar,
  HiDocumentText,
  HiArrowTopRightOnSquare,
  HiPlayCircle,
  HiOutlineClipboardDocumentList,
} from 'react-icons/hi2';

const CoachDashboardHome = ({ metrics, onNavigate }) => {
  const quickActions = useMemo(
    () => [
      {
        label: 'View Clients',
        description: 'Manage client relationships',
        icon: HiArrowTopRightOnSquare,
        target: 'crm',
      },
      {
        label: 'Training',
        description: 'Access training modules',
        icon: HiPlayCircle,
        target: 'training',
      },
      {
        label: 'Sessions',
        description: 'Schedule coaching sessions',
        icon: HiCalendarDays,
        target: 'session-calendar',
      },
      {
        label: 'Reports',
        description: 'View analytics & reports',
        icon: HiOutlineClipboardDocumentList,
        target: 'reports',
      },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        <header className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-black">Coach Dashboard</h1>
              <p className="text-gray-600">Manage your assigned clients and training programs</p>
            </div>
          </div>
        </header>

        <section className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Clients</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">{metrics.activeClients}</p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <HiUsers className="h-6 w-6" />
              </span>
            </div>
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Sessions This Week</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {metrics.sessionsThisWeek}
                </p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600">
                <HiCalendarDays className="h-6 w-6" />
              </span>
            </div>
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Client Score</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {metrics.averageClientScore}%
                </p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-50 text-purple-600">
                <HiChartBar className="h-6 w-6" />
              </span>
            </div>
          </article>

          <article className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Reports</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {metrics.pendingReports}
                </p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                <HiDocumentText className="h-6 w-6" />
              </span>
            </div>
          </article>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {quickActions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => onNavigate(action.target)}
                  className="group flex flex-col items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-500 hover:shadow-lg"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition group-hover:bg-blue-600 group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{action.label}</p>
                    <p className="mt-1 text-sm text-gray-500">{action.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

const CoachTraining = () => {
  const [activeCourse, setActiveCourse] = useState('business');
  const [userRole] = useState('coach');

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-black">Training Management</h1>
          <p className="text-gray-600">
            Monitor client progress and provide feedback on training modules
          </p>
        </div>

        <div className="mb-8 rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveCourse('business')}
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeCourse === 'business'
                    ? 'border-blue-600 text-black'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                }`}
              >
                <span>Design-A-Business</span>
              </button>
              <button
                onClick={() => setActiveCourse('costing')}
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeCourse === 'costing'
                    ? 'border-blue-600 text-black'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                }`}
              >
                <span>Introduction to Costing & Management Accounting</span>
              </button>
              <button
                onClick={() => setActiveCourse('capacity')}
                className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                  activeCourse === 'capacity'
                    ? 'border-blue-600 text-black'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-black'
                }`}
              >
                <span>Capacity Building Program</span>
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeCourse === 'business' && <DesignABusiness userRole={userRole} />}
            {activeCourse === 'costing' && <IntroToCostingAndManAcc userRole={userRole} />}
            {activeCourse === 'capacity' && <CapacityBuildingProgram userRole={userRole} />}
          </div>
        </div>
      </div>
    </div>
  );
};

const CoachesDashboard = () => {
  const { currentUser, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSection, setSelectedSection] = useState('dashboard');
  const [inboxSubsection, setInboxSubsection] = useState('public');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [reportsClientId, setReportsClientId] = useState(null);

  const dashboardMetrics = useMemo(
    () => ({
      activeClients: 12,
      sessionsThisWeek: 8,
      averageClientScore: 85,
      pendingReports: 3,
    }),
    []
  );

  const sectionPlaceholders = {
    dashboard: <CoachDashboardHome metrics={dashboardMetrics} onNavigate={setSelectedSection} />,
    training: <CoachTraining />,
    'product-development': <ProductDevelopment userRole="coach" />,
    'program-health-check': <ProgramHealthCheckDashboard userRole="coach" />,
    funding: (
      <div className="mx-auto max-w-5xl space-y-8 p-4">
        <GrantBasics userRole="coach" />
        <ApplicationTutorials userRole="coach" />
      </div>
    ),
    scalingmodules: <ScalingModules userRole="coach" />,
    commercialisation: <Commercialisation userRole="coach" />,
    'commercialisation-readiness-checklist': (
      <CommercialisationReadinessChecklist userRole="coach" />
    ),
    'go-to-market-strategy': <GTMStrategyPlan userRole="coach" />,
    'startup-launch-checklist': <StartupLaunchChecklist userRole="coach" />,
    'commercialisation-admin-panel': <AdminPanel />,
    'commercialisation-analytics': <AdminAnalyticsDashboard />,
    crm: <CoachesCRM />,

    'profiling-analytics': <ProfilingAnalyticsDashboard userRole="coach" />,
    'business-health-check': <HealthDashboard userRole="coach" />,
    'session-calendar': <SessionCalendar />,
    'session-history': <SessionHistory />,
    'session-notes': <SessionNotes />,
    'follow-up-tasks': <FollowUpTasks />,

    'coaching-tools': <CoachingTools />,
    'best-practices': <BestPractices />,
    'knowledge-base': <KnowledgeBase />,
    templates: <Templates />,

    'profile-settings': <SettingsPageComponent />,
    'account-security': <SettingsPageComponent />,
    'notification-preferences': <SettingsPageComponent />,
    'system-preferences': <SettingsPageComponent />,
    settings: <SettingsPageComponent />,

    reports: (
      <div className="p-6">
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Reports Hub</h1>
          <p className="text-gray-600">Analytics, reports, and performance metrics</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-gray-600">Reports and analytics will be displayed here.</p>
        </div>
      </div>
    ),
    'monthly-coaching-reports': (
      <MonthlyCoachingReport userRole="coach" clientId={reportsClientId} />
    ),
    'document-bank': (
      <ClientDocumentBrowser
        title="Client Document Bank"
        description="Access and support any client by viewing their shared documents instantly."
      />
    ),
    'team-management': (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-lg md:p-8">
        <EnhancedTeamManager userId={currentUser?.uid} isReadOnly={false} />
      </div>
    ),
    moderators: (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-lg md:p-8">
        <ModeratorsTable />
      </div>
    ),
    clients: (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-lg md:p-8">
        <ClientsTable />
      </div>
    ),
    alumni: (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-lg md:p-8">
        <AlumniTable />
      </div>
    ),
    'junk-management': <JunkDashboard />,
  };

  const renderContent = () => {
    if (selectedSection === 'communication') {
      return inboxSubsection === 'public' ? <PublicChat /> : <Mailbox />;
    }

    return sectionPlaceholders[selectedSection] || sectionPlaceholders.dashboard;
  };

  const confirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  // Listen for navigation events from CoachesCRM
  useEffect(() => {
    const handleNavigateToReports = event => {
      const { clientId } = event.detail;
      setReportsClientId(clientId);
      setSelectedSection('monthly-coaching-reports');
    };

    window.addEventListener('navigate-to-monthly-reports', handleNavigateToReports);
    return () => {
      window.removeEventListener('navigate-to-monthly-reports', handleNavigateToReports);
    };
  }, []);

  return (
    <div className="flex h-screen bg-white">
      <CoachesSidebar
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        selected={selectedSection}
        setSelected={setSelectedSection}
        inboxSubsection={inboxSubsection}
        onInboxSubsectionChange={setInboxSubsection}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <CoachesHeader user={currentUser} />
        <main className="flex-1 overflow-y-auto bg-white">{renderContent()}</main>
      </div>
      <LogoutConfirmation
        isOpen={showLogoutConfirm}
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
};

export default CoachesDashboard;
